import { useCallback, useMemo, useState } from 'react';
import { IAbandonedBusinessSelection } from '../../interfaces/abandoned-business/IAbandonedBusinessSelection';

const INITIAL_SELECTION: IAbandonedBusinessSelection = {
  selectedDishes: [],
  didNotTakeAnything: false,
  comment: '',
};

const useDishSelection = () => {
  const [selection, setSelection] =
    useState<IAbandonedBusinessSelection>(INITIAL_SELECTION);

  const toggleDish = useCallback((dishId: string) => {
    setSelection(prev => {
      const exists = prev.selectedDishes.find(d => d.dishId === dishId);
      if (exists) {
        return {
          ...prev,
          selectedDishes: prev.selectedDishes.filter(d => d.dishId !== dishId),
        };
      }
      return {
        ...prev,
        selectedDishes: [...prev.selectedDishes, { dishId, quantity: 1 }],
        didNotTakeAnything: false,
      };
    });
  }, []);

  const updateQuantity = useCallback((dishId: string, quantity: number) => {
    if (quantity <= 0) {
      setSelection(prev => ({
        ...prev,
        selectedDishes: prev.selectedDishes.filter(d => d.dishId !== dishId),
      }));
      return;
    }
    setSelection(prev => ({
      ...prev,
      selectedDishes: prev.selectedDishes.map(d =>
        d.dishId === dishId ? { ...d, quantity } : d,
      ),
    }));
  }, []);

  const setDidNotTakeAnything = useCallback((value: boolean) => {
    setSelection(prev => ({
      ...prev,
      didNotTakeAnything: value,
      selectedDishes: value ? [] : prev.selectedDishes,
    }));
  }, []);

  const setComment = useCallback((comment: string) => {
    setSelection(prev => ({ ...prev, comment }));
  }, []);

  const canSubmit = useMemo(() => {
    if (selection.didNotTakeAnything) return selection.comment.trim().length > 0;
    return selection.selectedDishes.length > 0;
  }, [selection.didNotTakeAnything, selection.comment, selection.selectedDishes.length]);

  return {
    selection,
    toggleDish,
    updateQuantity,
    setDidNotTakeAnything,
    setComment,
    canSubmit,
  };
};

export default useDishSelection;

